import { computed, Injectable, signal } from '@angular/core';

import type { DashboardProjectOption } from '../components/dashboard-project-select/dashboard-project-select.component';

export interface PageBreadcrumbIcon {
  logoUrl?: string | null;
  initials?: string;
  color?: string;
}

/** Makes a crumb a project picker: the header draws it as `app-dashboard-project-select`. */
export interface PageBreadcrumbSelect {
  options: readonly DashboardProjectOption[];
  /** The open project's id. */
  value: string;
  onSelect: (projectId: string) => void;
}

export interface PageBreadcrumbCrumb {
  label: string;
  /** Returns to the level before this crumb. */
  onBack: () => void;
  icon?: PageBreadcrumbIcon;
  select?: PageBreadcrumbSelect;
}

// Lets a page push "drill-in" crumbs (e.g. a selected project, then a site in it) up to the
// shared dashboard layout header, as a trail after the page title. Each page that sets crumbs
// owns clearing them — on its own back action, and in ngOnDestroy() as a safety net if the user
// navigates away mid-drill-down.
@Injectable({ providedIn: 'root' })
export class PageBreadcrumbService {
  private readonly _crumbs = signal<readonly PageBreadcrumbCrumb[]>([]);

  public readonly crumbs = this._crumbs.asReadonly();

  /** The innermost crumb, or `null` when there is none. Kept for callers that deal in one crumb. */
  public readonly crumb = computed(() => this._crumbs().at(-1) ?? null);

  public setCrumbs(crumbs: readonly PageBreadcrumbCrumb[]): void {
    this._crumbs.set(crumbs);
  }

  public setCrumb(crumb: PageBreadcrumbCrumb): void {
    this._crumbs.set([crumb]);
  }

  public clear(): void {
    this._crumbs.set([]);
  }
}
